import tokens from '@kluein/design-tokens';
import { Box, Text } from '@kluein/klue-ui';
import { Sparkle } from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { UserAvatar } from 'components/common/user-avatar';
import { Scroller } from 'components/layout/scroller';
import { getFinalPrompt } from 'pages/dashboard/utils/askKlue.utils';
import { setAskKlueWizardId, useAskKlueWizardId } from 'store/views';

import { askKlueWizardFlow } from './data/askKlueWizardFlow';
import { AskKlueQuestionWizard } from './partials/ask-klue-question-wizard';

import type {
  AskKlueModalWizardProps,
  AskKlueQuestionWizardProps,
  AskKlueWizardQuestionProps,
} from './AskKlueModalWizard.types';
import type { UserType } from 'api/api.types';
import type { RootState } from 'store/store.types';

function ModalQuestions(props: AskKlueModalWizardProps) {
  const { t } = useTranslation(['Dashboard']);
  const { questions, finalPrompt, handleAskKlue } = props;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestions, setCurrentQuestions] = useState<
    AskKlueQuestionWizardProps[]
  >(() => {
    if (questions?.length && questions?.length > 0) {
      return [
        {
          ...questions?.[currentQuestionIndex],
          isDisabled: false,
          answer: '',
        } as AskKlueQuestionWizardProps,
      ];
    }
    return [];
  });

  const handleAnswer = useCallback(
    (answer: string, id: string) => {
      const newQuestions = currentQuestions.map((question) => {
        if (question.id === id) {
          return {
            ...question,
            answer,
            isDisabled: true,
          };
        }
        return question;
      });
      setCurrentQuestions(() => {
        return [
          ...newQuestions,
          {
            ...questions?.[currentQuestionIndex + 1],
            isDisabled: false,
            answer: '',
          } as AskKlueQuestionWizardProps,
        ];
      });
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
    },
    [currentQuestionIndex, currentQuestions, questions],
  );

  useEffect(() => {
    if (currentQuestionIndex === questions?.length) {
      const finalPromptFormated = getFinalPrompt(currentQuestions, finalPrompt);
      handleAskKlue(finalPromptFormated);
      setAskKlueWizardId(undefined);
    }
  }, [
    currentQuestionIndex,
    currentQuestions,
    finalPrompt,
    questions?.length,
    handleAskKlue,
  ]);

  return (
    <Box
      pad="xlarge"
      background={tokens.color.neutral.white.main}
      round="xsmall"
      border={{ color: tokens.color.neutral.lightgrey.main }}
      flex={{ shrink: 0 }}
      margin={{ bottom: 'xlarge' }}
    >
      <Box direction="row" gap="small">
        <Sparkle
          size={tokens.iconSize.small}
          color={tokens.color.primary.main}
        />
        <Text
          size={tokens.fontSize.bodyFont.large}
          color={tokens.color.neutral.black.main}
          fontWeight="medium"
          fontFamily={tokens.fontFamily.Inter}
        >
          {t('Dashboard:askKlue.title')}
        </Text>
      </Box>
      {currentQuestions.map((question, index) => (
        <Box key={index}>
          <AskKlueQuestionWizard {...question} onAnswer={handleAnswer} />
        </Box>
      ))}
    </Box>
  );
}

function AskKlueModalWizard({
  handleAskKlue,
}: Pick<AskKlueModalWizardProps, 'handleAskKlue'>) {
  const boxRef = useRef<HTMLDivElement>(null);
  const wizardId = useAskKlueWizardId();

  const wizardData = askKlueWizardFlow.find((wizard) => wizard.id === wizardId);

  const user = useSelector<RootState, UserType | null>(
    (state) => state.auth.user,
  );

  useEffect(() => {
    if (boxRef.current) {
      boxRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, []);

  return (
    <Box>
      <Scroller disableFade>
        <Box
          height="100vh"
          pad={{ left: tokens.spacing.medium, vertical: 'xxlarge' }}
          gap="xxlarge"
        >
          <Box flex={{ grow: 1 }} ref={boxRef}>
            <Box flex={{ shrink: 0 }} margin={{ bottom: 'xxlarge' }}>
              <Box direction="row" align="center" gap="small">
                <UserAvatar
                  userId={user?.id as number}
                  size={24}
                  radius="xl"
                  showBorder={false}
                />
                <Text
                  size={tokens.fontSize.bodyFont.large}
                  color={tokens.color.primary.dark}
                  fontFamily={tokens.fontFamily.Proxima}
                  fontWeight="semibold"
                  lineHeight={tokens.lineHeight.dense}
                >
                  {user?.firstName}
                </Text>
              </Box>
              <Box pad={{ left: 'xxlarge' }}>
                <Text
                  size={tokens.fontSize.bodyFont.medium}
                  color={tokens.color.neutral.darkgrey.main}
                  lineHeight={tokens.lineHeight.dense}
                  fontFamily={tokens.fontFamily.Inter}
                  style={{
                    paddingLeft: tokens.spacing.xxsmall,
                    wordWrap: 'break-word',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {wizardData?.title}
                </Text>
              </Box>
            </Box>
            <ModalQuestions
              questions={wizardData?.questions as AskKlueWizardQuestionProps[]}
              finalPrompt={wizardData?.finalPrompt as string}
              handleAskKlue={handleAskKlue}
            />
          </Box>
        </Box>
      </Scroller>
    </Box>
  );
}

export default AskKlueModalWizard;
